import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton, useTheme } from '@mui/material'
import { Fullscreen, Refresh } from '@mui/icons-material'
import { com } from '@eidu/form'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import containerSrc from '../../../../../runtime-packages/runtime-container/dist/container.js?raw'
import useEntityFetcher from '../../../../io/useEntityFetcher'
import Entity = com.eidu.sharedlib.entity.Entity
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import type { RuntimeHost, RuntimeTheme, ValidationResult } from 'runtimes-shared'
import fetchService from '../../../../io/api/fetchService'
import { useSnackBar } from '../../../../components/feedback/SnackBarProvider'

const srcDoc = `<html><body><script>${containerSrc}</script></body></html>`

const waitForRuntimeHost = (win: Window & { runtimeHost?: RuntimeHost }): Promise<RuntimeHost> => {
  if (win.runtimeHost) return Promise.resolve(win.runtimeHost)
  return new Promise((resolve, reject) => {
    let attempts = 0
    const interval = setInterval(() => {
      if (win.runtimeHost) {
        clearInterval(interval)
        resolve(win.runtimeHost)
      } else if (++attempts > 50) {
        clearInterval(interval)
        reject(new Error('runtimeHost not available after timeout'))
      }
    }, 50)
  })
}

export const useLocalRuntimes = import.meta.env.VITE_LOCAL_RUNTIMES === 'true'

export const AssetPreviewView = ({
  entity,
  payload,
  entityKind,
  entityTypes,
  onValidationChange,
  onRefresh,
  onLog,
}: {
  entity: EntityWithLabelAndRelated
  payload?: string
  entityKind?: string
  entityTypes?: ReadonlyMap<EntityTypeId, EntityType>
  onValidationChange?: (result: ValidationResult) => void
  onRefresh?: () => void
  onLog?: (level: 'log' | 'warn' | 'error', args: unknown[]) => void
}) => {
  const muiTheme = useTheme()
  const runtimeTheme: RuntimeTheme = { primaryColor: muiTheme.palette.primary.main }
  const { showSnackBar } = useSnackBar()

  const containerRef = useRef<HTMLDivElement>(null)
  const containerWindowRef = useRef<(Window & { runtimeHost: RuntimeHost }) | null>(null)
  const payloadRef = useRef(payload)
  payloadRef.current = payload
  const [fullscreen, setFullscreen] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)

  const fetchById = useEntityFetcher()

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      containerRef.current?.requestFullscreen()
    }
  }, [])

  const runRuntimeHost = useCallback(
    (runtimeHost: RuntimeHost) => {
      const getEntityJson = (id: string) => {
        const entityId = entityIdFromStringOrNull(id)
        if (!entityId) return Promise.reject(new Error(`Invalid entity id: ${id}`))
        return fetchById(entityId).then((e) => {
          if (!e) throw new Error(`Entity not found: ${id}`)
          return Entity.toJsonString(e.entity)
        })
      }

      runtimeHost.run({
        entityJson: Entity.toJsonString(entity.entity),
        getEntityJson,
        fetchService,
        onLoad: () => {
          onValidationChange?.(runtimeHost.validate())
        },
        onLog,
        openAsset: (entityId: string) => {
          const eid = entityIdFromStringOrNull(entityId)
          if (!eid) {
            showSnackBar(`Invalid entity id: ${entityId}`, 'error')
            return
          }
          fetchById(eid)
            .then((e) => {
              if (!e) throw new Error(`Entity not found: ${entityId}`)
              window.open(`/entities/${e.entity.typeId}/view/${entityId}`, '_blank')
            })
            .catch((err) => {
              console.error('[AssetPreview] openAsset failed:', err)
              showSnackBar(`Could not open entity ${entityId}`, 'error')
            })
        },
        theme: runtimeTheme,
      })
    },
    [entity, fetchById]
  )

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const win = e.currentTarget.contentWindow as (Window & { runtimeHost: RuntimeHost }) | null
      if (!win) return
      containerWindowRef.current = win
      if (useLocalRuntimes && entityTypes) {
        const typeIdToKind: Record<string, string> = {}
        for (const [id, type] of entityTypes) {
          const kind = getKnownKindKey(type.kind)
          if (kind) typeIdToKind[id.toString()] = kind
        }
        win.__typeIdToKind = typeIdToKind
        // Module scripts in host.html may execute after the iframe load event
        waitForRuntimeHost(win).then(runRuntimeHost)
      } else {
        runRuntimeHost(win.runtimeHost)
      }
    },
    [entity, fetchById, entityTypes, runRuntimeHost]
  )

  return (
    <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {!fullscreen && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => {
              onRefresh?.()
              setIframeKey((k) => k + 1)
            }}
            size="small"
          >
            <Refresh />
          </IconButton>
          <IconButton onClick={toggleFullscreen} size="small">
            <Fullscreen />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          flex: 1,
          minHeight: 0,
          bgcolor: fullscreen ? 'background.default' : undefined,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {useLocalRuntimes && entityKind ? (
          <iframe
            key={iframeKey}
            title="Asset preview"
            src="/runtime-packages/runtime-container/host.html"
            onLoad={handleLoad}
            style={{ flex: 1, width: '100%', minHeight: 200, border: 'none' }}
          />
        ) : (
          <iframe
            key={iframeKey}
            title="Asset preview"
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-same-origin"
            onLoad={handleLoad}
            style={{ flex: 1, width: '100%', minHeight: 200, border: 'none' }}
          />
        )}
      </Box>
    </Box>
  )
}
